/* =========================================================================
   Site identity / app store links / contact / social fields for the Settings
   page, plus client-side validation.

   The rules MIRROR the backend's src/utils/configAppInfo.js (media-platform)
   so staff see the same message inline before saving that the API would
   return as a 422. The backend stays the source of truth: if it rejects a
   save anyway, its per-field `errors` map is shown under the matching input.
   ========================================================================= */

const URL_MAX = 2048;

const isHost = (...hosts) => (h) => hosts.includes(h);
const isAmazon = (h) => /(^|\.)amazon\.[a-z]{2,3}(\.[a-z]{2})?$/.test(h);

export const APP_INFO_SECTIONS = [
  {
    key: "site_identity",
    title: "Site identity",
    fields: [
      { name: "site_title", label: "Site title", type: "text", max: 70, counter: true, placeholder: "My Streaming Service" },
      { name: "meta_description", label: "Meta description", type: "textarea", max: 160, counter: true, placeholder: "Shown by search engines and link previews" },
      { name: "favicon_url", label: "Favicon URL", type: "url", preview: true, upload: true, placeholder: "https://cdn.example.com/favicon.png" },
      { name: "app_logo_url", label: "App logo URL", type: "url", preview: true, upload: true, placeholder: "https://cdn.example.com/logo.png" },
      { name: "copyright_text", label: "Copyright text", type: "text", max: 200, counter: true, placeholder: "© {year} Company, Inc. All rights reserved." },
    ],
  },
  {
    key: "app_store_links",
    title: "App store links",
    fields: [
      { name: "ios_app_url", label: "iOS", type: "url", icon: "ios", openLink: true, host: isHost("apps.apple.com"), hostLabel: "apps.apple.com", placeholder: "https://apps.apple.com/…" },
      { name: "android_app_url", label: "Android", type: "url", icon: "android", openLink: true, host: isHost("play.google.com"), hostLabel: "play.google.com", placeholder: "https://play.google.com/store/apps/details?id=…" },
      { name: "roku_app_url", label: "Roku", type: "url", icon: "roku", openLink: true, host: isHost("channelstore.roku.com"), hostLabel: "channelstore.roku.com", placeholder: "https://channelstore.roku.com/details/…" },
      { name: "firetv_app_url", label: "Fire TV", type: "url", icon: "firetv", openLink: true, host: isAmazon, hostLabel: "an amazon.* domain", placeholder: "https://www.amazon.com/dp/…" },
      { name: "appletv_app_url", label: "Apple TV", type: "url", icon: "appletv", openLink: true, host: isHost("apps.apple.com"), hostLabel: "apps.apple.com", placeholder: "https://apps.apple.com/…" },
      { name: "samsung_tv_app_url", label: "Samsung TV", type: "url", icon: "samsung", openLink: true, placeholder: "https://…" },
      { name: "lg_tv_app_url", label: "LG TV", type: "url", icon: "lg", openLink: true, placeholder: "https://…" },
    ],
  },
  {
    key: "contact",
    title: "Contact",
    fields: [
      { name: "support_email", label: "Support email", type: "email", max: 254, icon: "email", placeholder: "support@example.com" },
      { name: "support_phone", label: "Support phone", type: "phone", max: 30, icon: "phone", placeholder: "+1 555 123 4567" },
      { name: "whatsapp_number", label: "WhatsApp number", type: "phone", max: 30, icon: "whatsapp", placeholder: "+1 555 123 4567" },
      { name: "company_address", label: "Company address", type: "textarea", max: 300, counter: true, icon: "address", placeholder: "Street, City, Country" },
    ],
  },
  {
    key: "social",
    title: "Social",
    fields: [
      { name: "facebook_url", label: "Facebook", type: "url", icon: "facebook", openLink: true, placeholder: "https://facebook.com/…" },
      { name: "instagram_url", label: "Instagram", type: "url", icon: "instagram", openLink: true, placeholder: "https://instagram.com/…" },
      { name: "x_url", label: "X", type: "url", icon: "x", openLink: true, placeholder: "https://x.com/…" },
      { name: "youtube_url", label: "YouTube", type: "url", icon: "youtube", openLink: true, placeholder: "https://youtube.com/@…" },
      { name: "linkedin_url", label: "LinkedIn", type: "url", icon: "linkedin", openLink: true, placeholder: "https://linkedin.com/company/…" },
      { name: "tiktok_url", label: "TikTok", type: "url", icon: "tiktok", openLink: true, placeholder: "https://tiktok.com/@…" },
    ],
  },
];

export const APP_INFO_FIELDS = APP_INFO_SECTIONS.flatMap((s) => s.fields);
export const APP_INFO_FIELD_NAMES = APP_INFO_FIELDS.map((f) => f.name);
const FIELD_BY_NAME = Object.fromEntries(APP_INFO_FIELDS.map((f) => [f.name, f]));

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9\s().-]+$/;

/** Max length for a field (URLs default to 2048, like the backend). */
export const maxLengthFor = (field) =>
  field.max || (field.type === "url" ? URL_MAX : undefined);

/** True if the value parses as an absolute https URL (used for previews / Open). */
export const isHttpsUrl = (value) => {
  try {
    const url = new URL(String(value || "").trim());
    return url.protocol === "https:" && url.hostname.includes(".");
  } catch {
    return false;
  }
};

/**
 * Validate one field's value. Returns an error message, or "" when valid.
 * Empty / whitespace is always valid (it saves as null).
 */
export const validateAppInfoField = (name, raw) => {
  const field = FIELD_BY_NAME[name];
  if (!field) return "";
  const value = raw == null ? "" : String(raw).trim();
  if (value === "") return "";

  const max = maxLengthFor(field);
  if (max && value.length > max) return `${field.label} must be at most ${max} characters`;

  if (field.type === "url") {
    let url;
    try {
      url = new URL(value);
    } catch {
      return `${field.label} must be a valid URL`;
    }
    if (url.protocol !== "https:") return `${field.label} must be an https:// URL`;
    if (!url.hostname || !url.hostname.includes(".")) return `${field.label} must be a valid URL`;
    if (field.host && !field.host(url.hostname.toLowerCase())) {
      return `${field.label} must be a link on ${field.hostLabel}`;
    }
  }

  if (field.type === "email" && !EMAIL_RE.test(value)) {
    return `${field.label} must be a valid email address`;
  }

  if (field.type === "phone") {
    const digits = value.replace(/\D/g, "").length;
    if (!PHONE_RE.test(value) || digits < 5 || digits > 15) {
      return `${field.label} must be a valid phone number (digits, spaces, + ( ) - only)`;
    }
  }

  return "";
};

/** Validate several fields. Returns { name: message } for the invalid ones only. */
export const validateAppInfoValues = (values, names = Object.keys(values)) => {
  const errors = {};
  for (const name of names) {
    const msg = validateAppInfoField(name, values[name]);
    if (msg) errors[name] = msg;
  }
  return errors;
};

/** Values as the API should receive them: trimmed, empty -> null. */
export const toPayload = (values, names) =>
  Object.fromEntries(
    names.map((n) => {
      const v = values[n] == null ? "" : String(values[n]).trim();
      return [n, v === "" ? null : v];
    })
  );

/** Form state from a /config response: null/undefined -> "". */
export const fromConfig = (config) =>
  Object.fromEntries(APP_INFO_FIELD_NAMES.map((n) => [n, config?.[n] ?? ""]));

/**
 * Suggested values for still-empty fields, from what SuperAdmin already knows
 * about the tenant (name, logo, contact email). Only fills fields with no saved
 * value, and nothing is persisted until staff press the section's Save.
 */
export const prefillFromTenant = (tenant, config) => {
  if (!tenant) return {};
  const name = String(tenant.name || "").trim();
  const logo = String(tenant.logoUrl || "").trim();
  const email = String(tenant.contactEmail || "").trim();
  const suggested = {
    site_title: name.slice(0, 70),
    app_logo_url: isHttpsUrl(logo) ? logo : "",
    copyright_text: name ? `© {year} ${name}. All rights reserved.` : "",
    support_email: email,
  };
  return Object.fromEntries(
    Object.entries(suggested).filter(
      ([n, v]) => v && String(config?.[n] ?? "").trim() === "" && !validateAppInfoField(n, v)
    )
  );
};
